import React, { useEffect, useState } from "react";
import { BookOpen, Printer, ChevronRight, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PARTS, SECTIONS, ROLE_META } from "@/lib/guideContent";

const RoleTags = ({ roles }) => (
  <div className="flex flex-wrap gap-1.5">
    {roles.map((r) => (
      <span key={r} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${ROLE_META[r].cls}`}>{ROLE_META[r].label}</span>
    ))}
  </div>
);

const Step = ({ step, n }) => (
  <figure className="guide-step mt-8 break-inside-avoid">
    <figcaption className="flex items-baseline gap-3 mb-3">
      <span className="mono text-xs text-emerald-700 font-semibold">STEP {n}</span>
      <span className="text-base font-semibold tracking-tight">{step.title}</span>
    </figcaption>
    {step.text && <p className="text-sm text-slate-600 mb-3 max-w-3xl">{step.text}</p>}
    <img src={`/guide/${step.img}`} alt={step.title} loading="lazy" width={1440} height={900} data-testid={`guide-img-${step.img.replace(".jpg", "")}`}
      className="w-full h-auto max-w-4xl rounded-md border border-slate-200 shadow-sm" />
    {step.callouts && (
      <ol className="mt-4 space-y-2 max-w-3xl">
        {step.callouts.map((c, i) => (
          <li key={c} className="flex gap-3 text-sm text-slate-700">
            <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold grid place-items-center">{i + 1}</span>
            <span className="pt-0.5">{c}</span>
          </li>
        ))}
      </ol>
    )}
  </figure>
);

const Section = ({ s }) => (
  <section id={s.id} data-testid={`guide-section-${s.id}`} className="guide-section scroll-mt-6 pt-10 mt-10 border-t border-slate-200">
    <div className="flex flex-wrap items-center gap-3 mb-2">
      <h3 className="text-xl font-semibold tracking-tight">{s.title}</h3>
      <RoleTags roles={s.roles} />
    </div>
    {s.intro && <p className="text-sm text-slate-600 max-w-3xl">{s.intro}</p>}
    {s.steps.map((st, i) => <Step key={`${st.img}-${i}`} step={st} n={i + 1} />)}
    {s.tips && (
      <div className="mt-6 max-w-3xl bg-amber-50 border border-amber-200 rounded-md p-4 break-inside-avoid">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-800 font-semibold mb-2"><Lightbulb className="w-4 h-4" /> Good to know</div>
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">{s.tips.map((t) => <li key={t}>{t}</li>)}</ul>
      </div>
    )}
  </section>
);

const Part = ({ part, idx }) => (
  <div id={`part-${part.id}`} data-testid={`guide-part-${part.id}`} className="guide-part scroll-mt-6 mt-16 first:mt-8">
    <div className="grid md:grid-cols-[1fr_320px] gap-6 items-center bg-white border border-slate-200 rounded-md p-6 break-inside-avoid">
      <div>
        <div className="mono text-xs text-emerald-700 font-semibold">PART {idx + 1}</div>
        <h2 className="text-3xl font-semibold tracking-tight mt-1">{part.title}</h2>
        <p className="text-sm text-slate-600 mt-2">{part.blurb}</p>
      </div>
      <img src={`/guide/${part.cover}`} alt="" width={1100} height={738} className="w-full h-auto rounded-md" />
    </div>
    {SECTIONS.filter((s) => s.part === part.id).map((s) => <Section key={s.id} s={s} />)}
  </div>
);

const Toc = ({ active, onPick }) => (
  <nav data-testid="guide-toc" className="guide-toc hidden xl:block sticky top-6 self-start w-64 shrink-0 max-h-[calc(100vh-3rem)] overflow-y-auto text-sm">
    {PARTS.map((p, i) => (
      <div key={p.id} className="mb-4">
        <a href={`#part-${p.id}`} className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Part {i + 1} · {p.title}</a>
        {SECTIONS.filter((s) => s.part === p.id).map((s) => (
          <a key={s.id} href={`#${s.id}`} data-testid={`guide-toc-${s.id}`} onClick={() => onPick(s.id)}
            className={`flex items-center gap-1 py-1 pl-2 border-l-2 transition-colors ${active === s.id ? "border-emerald-600 text-zinc-950 font-medium" : "border-transparent text-slate-600 hover:text-blue-700"}`}>
            {active === s.id && <ChevronRight className="w-3 h-3" />}{s.title.replace(/^\d+\.\s*/, "")}
          </a>
        ))}
      </div>
    ))}
  </nav>
);

export default function UserGuide() {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting);
      if (vis.length) setActive(vis[0].target.id);
    }, { rootMargin: "0px 0px -70% 0px" });
    SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="flex gap-10" data-testid="user-guide-page">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Help</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-1">User Guide</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">Everything in KMS-Obligo, step by step — from signing in to signing out. Each section is tagged with who it applies to.</p>
          </div>
          <div className="guide-noprint flex flex-col items-end gap-1">
            <Button data-testid="guide-download-pdf" onClick={() => window.print()} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
              <Printer className="w-4 h-4" /> Download as PDF
            </Button>
            <span className="text-[11px] text-slate-500">Choose “Save as PDF” in the print window</span>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2 guide-noprint">
          {Object.entries(ROLE_META).map(([k, v]) => <span key={k} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${v.cls}`}>{v.label}</span>)}
        </div>
        <ol data-testid="guide-contents" className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PARTS.map((p, i) => (
            <li key={p.id}>
              <a href={`#part-${p.id}`} data-testid={`guide-contents-${p.id}`} className="block h-full bg-white border border-slate-200 rounded-md p-4 hover:border-blue-600 transition-colors">
                <div className="mono text-[11px] text-emerald-700 font-semibold">PART {i + 1}</div>
                <div className="font-semibold tracking-tight mt-0.5">{p.title}</div>
                <div className="text-xs text-slate-500 mt-1">{p.blurb}</div>
              </a>
            </li>
          ))}
        </ol>
        {PARTS.map((p, i) => <Part key={p.id} part={p} idx={i} />)}
        <div className="guide-noprint h-[60vh]" aria-hidden="true" />
      </div>
      <Toc active={active} onPick={setActive} />
    </div>
  );
}
